import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { listUsersDirect } from '@timetable/api';
import { UserFilters } from '../components/superadmin/UserFilters';
import { UserList } from '../components/superadmin/UserList';
import { queryKeys } from '../lib/queryKeys';
import type { User, UserFilters as UserFiltersType } from '@timetable/types';

/**
 * UserManagementPage Component
 * 
 * Main page for Super Admin user management:
 * - Page header
 * - UserFilters component for filtering users
 * - UserList component for displaying and managing users
 * - Loading and error states
 * 
 * Requirements: 2.4.1, 3.4.1
 */
export function UserManagementPage() {
  const [filters, setFilters] = useState<UserFiltersType & { search?: string }>({});

  // Fetch users with filters and caching
  // Requirements: 4.1.3 - User list supports pagination for large datasets
  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.users.list({
      schoolId: filters.schoolId,
      role: filters.role,
      status: filters.status,
    }),
    queryFn: () =>
      listUsersDirect({
        schoolId: filters.schoolId,
        role: filters.role,
        status: filters.status,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter than schools since user data changes more frequently
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
  });

  // Client-side search filtering (by name or email)
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!filters.search) return users;

    const searchLower = filters.search.toLowerCase();
    return users.filter(
      (user: User) =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
    );
  }, [users, filters.search]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Stack mb={3}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage users across all schools - view, promote, demote, or remove users
        </Typography>
      </Stack>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load users: {(error as Error).message}
        </Alert>
      )}

      {/* Filters */}
      <UserFilters filters={filters} onChange={setFilters} />

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* User List */}
      {!isLoading && <UserList users={filteredUsers} isLoading={isLoading} />}
    </Box>
  );
}
